import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2, RefreshCcw } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { subscriptionService, type SubscriptionListItem } from '@/services/subscription.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues { amount: string; notes: string }

interface Props {
  subscription: SubscriptionListItem | null;
  onClose: () => void;
}

export function RenewDialog({ subscription, onClose }: Props) {
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: { amount: '', notes: '' },
  });

  useEffect(() => {
    if (subscription) {
      reset({ amount: String(subscription.amount), notes: '' });
    }
  }, [subscription, reset]);

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      subscriptionService.renew(subscription!.id, {
        amount: v.amount ? parseFloat(v.amount) : undefined,
        notes: v.notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Subscription renewed successfully');
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={!!subscription} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCcw className="h-4 w-4 text-primary" />
            Renew Subscription
          </DialogTitle>
        </DialogHeader>
        {subscription && (
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
            <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm">
              <p className="font-medium">{subscription.name}</p>
              {subscription.vendor_name && (
                <p className="text-muted-foreground">{subscription.vendor_name}</p>
              )}
              <p className="mt-1 text-muted-foreground">
                Billing cycle: <span className="font-medium text-foreground">{subscription.billing_cycle_name}</span>
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="renew-amount">
                Amount ({subscription.currency_symbol})
                <span className="ml-1 text-xs text-muted-foreground">(leave unchanged to use current amount)</span>
              </Label>
              <Input
                id="renew-amount"
                type="number"
                step="0.01"
                min={0}
                {...register('amount', {
                  validate: (v) => !v || parseFloat(v) > 0 || 'Amount must be positive',
                })}
                placeholder={String(subscription.amount)}
              />
              {errors.amount && <p className="text-xs text-destructive">{errors.amount.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="renew-notes">Notes</Label>
              <Textarea id="renew-notes" {...register('notes')} placeholder="Optional renewal notes" rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Renew
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
